import React from "react";
import styled, { css, keyframes } from "styled-components";
import { FiCheck, FiCopy, FiExternalLink, FiLoader } from "react-icons/fi";

import { CFlex, DText, Flex } from "../../../../../styles/CommonStyles";
import { UserDetailSettingBox } from "../../../../../styles/main/user";
import ActionButton from "../../../../common/buttons/ActionButton";
import {
    useCreateDueAccount,
    useGetUserKycLink,
} from "../../../../../core/services/react-query/user";
import { useGetAllAccounts } from "../../../../../core/services/react-query/report/due-payments";

const kycStatusConfig = {
    CREATED: {
        label: "KYC Not Started",
        description: "The user has not completed the KYC verification yet.",
    },
    PENDING: {
        label: "KYC Pending",
        description: "The user's KYC verification is currently being reviewed.",
    },
    VERIFIED: {
        label: "KYC Verified",
        description: "The user's KYC verification has been successfully completed.",
    },
    REJECTED: {
        label: "KYC Rejected",
        description: "The user's KYC verification was rejected.",
    },
};

const CreateDueAccount = ({ user }) => {
    const [userId, setUserId] = React.useState(null);
    const [copied, setCopied] = React.useState(false);
    const { data: createDueAccountData, mutate: createDueAccount, isLoading: isCreateDueAccountLoading } = useCreateDueAccount();
    const { data: userKycLink, isLoading: isGetUserKycLinkLoading } = useGetUserKycLink(userId);
    const { data: userDueAccount, isLoading: isGetUserDueAccountLoading } = useGetAllAccounts({ userId: user?._id });
    const dueAccountCreated = !!userId || (!!userDueAccount?.data.length > 0) || !!createDueAccountData;
    console.log(user?.status);
    // ONE OF BELOW:
    // VERIFIED
    // PENDING
    // CREATED
    // REJECTED

    const handleCreateDueAccount = () => {
        createDueAccount({
            userId: user?._id,
            payload: {
                isBusiness: user?.isBusiness,
                countryCode: user?.countryCode,
                businessName: user?.businessName,
            },
        }, {
            onSuccess: (data) => {
                const createdUserId = data?.userId || data?.id || data?.data?.userId || data?.data?.id;
                if (createdUserId) setUserId(createdUserId);
            },
        });
    };

    const handleGetKycLink = () => {
        setUserId(user?._id);
    };

    const handleCopyLink = async () => {
        if (!userKycLink) return;
        await navigator.clipboard.writeText(userKycLink);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const kycStatus = kycStatusConfig[user?.status] || {
        label: "Unknown",
        description: "The current KYC status is unavailable.",
    };


    return (
        <CFlex fw align="center">
            <UserDetailSettingBox className="w-full max-w-[600px]">
                <SectionTitle main>KYC Account Setup</SectionTitle>
                <SectionDescription main>
                    Create a Due account for this user and generate a KYC verification link that can be shared with them.
                </SectionDescription>

                <StepCard completed={dueAccountCreated}>
                    <StepHeader>
                        <Flex align="flex-start" justify="flex-start" gap="12px">
                            <StepNumber completed={dueAccountCreated}>{dueAccountCreated ? <FiCheck size={16} /> : "1"}</StepNumber>
                            <StepText>
                                <StepTitle>Create Due Account</StepTitle>
                                <StepDescription>Create the Due account using the user's existing profile information.</StepDescription>
                            </StepText>
                        </Flex>
                        {!dueAccountCreated && (
                            <ActionButton active width="150px" height="42px" onClick={handleCreateDueAccount} loading={isCreateDueAccountLoading || isGetUserDueAccountLoading} disabled={isCreateDueAccountLoading || isGetUserDueAccountLoading}>
                                <DText >
                                    Create Account
                                </DText>
                            </ActionButton>
                        )}
                    </StepHeader>
                </StepCard>

                {dueAccountCreated && (
                    <StepCard>
                        <StepHeader>
                            <Flex align="flex-start" justify="flex-start" gap="12px">
                                <StepNumber completed={user?.status === "VERIFIED"}>
                                    {user?.status === "VERIFIED" ? (
                                        <FiCheck size={16} />
                                    ) : (
                                        "2"
                                    )}
                                </StepNumber>

                                <StepText>
                                    <Flex align="center" gap="8px" wrap="wrap">
                                        <StepTitle>KYC Verification</StepTitle>

                                        <span
                                            className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${user?.status === "VERIFIED"
                                                    ? "bg-green-500/10 text-green-500"
                                                    : user?.status === "PENDING"
                                                        ? "bg-yellow-500/10 text-yellow-500"
                                                        : user?.status === "REJECTED"
                                                            ? "bg-red-500/10 text-red-500"
                                                            : "bg-gray-500/10 text-gray-400"
                                                }`}
                                        >
                                            {kycStatus.label}
                                        </span>
                                    </Flex>

                                    <StepDescription>
                                        {kycStatus.description}
                                    </StepDescription>
                                </StepText>
                            </Flex>

                            {user?.status !== "VERIFIED" &&
                                user?.status !== "PENDING" &&
                                !userKycLink && (
                                    <SecondaryButton
                                        type="button"
                                        onClick={handleGetKycLink}
                                        disabled={isGetUserKycLinkLoading}
                                    >
                                        {isGetUserKycLinkLoading && <LoadingIcon size={15} />}
                                        {isGetUserKycLinkLoading
                                            ? "Generating..."
                                            : "Get KYC Link"}
                                    </SecondaryButton>
                                )}
                        </StepHeader>

                        {userKycLink && user?.status !== "VERIFIED" && (
                            <LinkBox>
                                <LinkText>
                                    <LinkLabel>KYC Verification Link</LinkLabel>

                                    <LinkValue title={userKycLink}>
                                        {userKycLink}
                                    </LinkValue>
                                </LinkText>

                                <LinkActions>
                                    <SecondaryButton
                                        type="button"
                                        onClick={handleCopyLink}
                                    >
                                        {copied ? (
                                            <FiCheck size={14} />
                                        ) : (
                                            <FiCopy size={14} />
                                        )}

                                        {copied ? "Copied" : "Copy"}
                                    </SecondaryButton>

                                    <OpenLink
                                        href={userKycLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        title="Open KYC link"
                                    >
                                        <FiExternalLink size={15} />
                                    </OpenLink>
                                </LinkActions>
                            </LinkBox>
                        )}
                    </StepCard>
                )}
            </UserDetailSettingBox>
        </CFlex>
    );
};

const SectionTitle = styled(DText)`
    font-size: 0.95rem;
    font-weight: 600;
    margin-bottom: 6px;
`;

const SectionDescription = styled(DText)`
    color: ${(props) => props.theme.color}a0;
    font-size: 0.75rem;
    line-height: 1.8;
    margin-bottom: 18px;
`;

const StepCard = styled.div`
    width: 100%;
    padding: 14px;
    margin-bottom: 12px;
    border: 1px solid ${(props) => props.theme.color}20;
    border-radius: 4px;
    background-color: ${(props) => props.theme.secondaryBg};

    ${(props) => props.completed && css`
        border-color: ${props.theme.mainGreen}70;
        background-color: ${props.theme.mainGreen}0d;
    `}
`;

const StepHeader = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;

    @media screen and (max-width: 480px) {
        flex-direction: column;
        > button { width: 100%; }
    }
`;

const StepNumber = styled.div`
    width: 30px;
    height: 30px;
    flex-shrink: 0;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: ${(props) => props.completed ? `${props.theme.mainGreen}25` : `${props.theme.active}30`};
    color: ${(props) => props.completed ? props.theme.mainGreen : props.theme.active};
    font-size: 0.8rem;
    font-weight: 600;
`;

const StepText = styled.div`min-width: 0;`;

const StepTitle = styled.div`
    color: ${(props) => props.theme.color};
    font-size: 0.82rem;
    font-weight: 600;
`;

const StepDescription = styled.div`
    color: ${(props) => props.theme.color}a0;
    font-size: 0.7rem;
    line-height: 1.7;
    margin-top: 4px;
`;

const SecondaryButton = styled.button`
    min-width: 122px;
    height: 42px;
    padding: 0 12px;
    border: 1px solid ${(props) => props.theme.color}35;
    border-radius: 4px;
    background-color: ${(props) => props.theme.mainBg};
    color: ${(props) => props.theme.color};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    font-family: Vazir;
    font-size: 0.72rem;
    cursor: pointer;
    transition: all 0.2s;

    &:hover:not(:disabled) {
        background-color: ${(props) => props.theme.tInputBg};
        border-color: ${(props) => props.theme.color}70;
    }
    &:disabled { cursor: not-allowed; opacity: 0.6; }
`;

const spin = keyframes`to { transform: rotate(360deg); }`;
const LoadingIcon = styled(FiLoader)`animation: ${spin} 0.9s linear infinite;`;

const LinkBox = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px;
    margin-top: 14px;
    border: 1px solid ${(props) => props.theme.color}20;
    border-radius: 4px;
    background-color: ${(props) => props.theme.mainBg};

    @media screen and (max-width: 480px) { align-items: stretch; flex-direction: column; }
`;

const LinkText = styled.div`min-width: 0; flex: 1;`;
const LinkLabel = styled.div`
    color: ${(props) => props.theme.color}a0;
    font-size: 0.65rem;
    margin-bottom: 5px;
`;
const LinkValue = styled.div`
    overflow: hidden;
    color: ${(props) => props.theme.color};
    font-family: monospace;
    font-size: 0.72rem;
    text-overflow: ellipsis;
    white-space: nowrap;
    direction: ltr;
    text-align: left;
`;
const LinkActions = styled.div`
    display: flex;
    align-items: center;
    gap: 7px;
    ${SecondaryButton} { min-width: auto; }
`;
const OpenLink = styled.a`
    width: 42px;
    height: 42px;
    border: 1px solid ${(props) => props.theme.color}35;
    border-radius: 4px;
    background-color: ${(props) => props.theme.mainBg};
    color: ${(props) => props.theme.color};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;

    &:hover {
        background-color: ${(props) => props.theme.tInputBg};
        border-color: ${(props) => props.theme.color}70;
    }
`;

export default CreateDueAccount;
